import { useEffect, useRef } from "react";
import { socketUrl } from "../services/socketUrl";

// Reconnect backoff: quick enough that a brief drop is invisible, capped so
// a server that is down does not get hammered by every open tab.
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

interface UseChatSocketProps {
  token: string;
  smsThreadId?: string;
  onRefresh: (socketData?: any) => void | Promise<void>;
}

const shouldRefreshFromSocket = (data: any) => {
  if (!data || typeof data !== "object") return false;

  const type = data?.type;
  const event = data?.event;

  if (
    type === "message" ||
    event === "message" ||
    type === "inbox_updated" ||
    event === "inbox_updated" ||
    type === "new_message" ||
    event === "new_message"
  ) {
    return true;
  }

  // Thread-scoped payloads should still trigger a refresh.
  if (data?.sms_thread_id != null) {
    return true;
  }

  return false;
};

export const useChatWebSocket = ({
  token,
  smsThreadId,
  onRefresh,
}: UseChatSocketProps) => {
  const socketRef = useRef<WebSocket | null>(null);
  const onRefreshRef = useRef(onRefresh);
  const smsThreadIdRef = useRef(smsThreadId);

  onRefreshRef.current = onRefresh;
  smsThreadIdRef.current = smsThreadId;

  const joinActiveThread = (socket: WebSocket) => {
    const threadId = smsThreadIdRef.current;

    if (!threadId || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    socket.send(
      JSON.stringify({
        type: "join",
        sms_thread_id: threadId,
      })
    );

    console.log("JOINING THREAD:", threadId);
  };

  useEffect(() => {
    if (!token) return;

    const actualToken = atob(token);

    // The socket used to be opened once and never reopened: onclose only
    // logged. A sleeping laptop, a change of network, an idle timeout at the
    // proxy or a baseapp restart left Messages connected to nothing, and the
    // user sat on a quiet inbox that was simply no longer receiving. Reconnect
    // with a backoff, and refresh once on reconnect so anything missed while
    // the socket was down is picked up.
    let isUnmounted = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let hasConnectedBefore = false;

    const backoffMs = () =>
      Math.min(INITIAL_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS);

    const connect = () => {
      if (isUnmounted) return;

      const socket = new WebSocket(socketUrl("ws/chat/", actualToken));
      socketRef.current = socket;

      socket.onopen = () => {
        attempt = 0;
        joinActiveThread(socket);
        // A reconnect means time passed with no live updates, so pull the
        // current state rather than waiting for the next event.
        if (hasConnectedBefore) {
          void onRefreshRef.current();
        }
        hasConnectedBefore = true;
      };

      socket.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (shouldRefreshFromSocket(data)) {
            await onRefreshRef.current(data);
          }
        } catch (error) {
          console.error("Chat WebSocket parse error:", error);
        }
      };

      socket.onerror = (error) => {
        console.error("Chat WebSocket error:", error);
      };

      socket.onclose = (event) => {
        if (isUnmounted) return;
        // 1000 is a clean close; anything else means we lost the connection.
        console.warn("Chat WebSocket closed", event.code, event.reason);
        reconnectTimer = setTimeout(connect, backoffMs());
        attempt += 1;
      };
    };

    connect();

    return () => {
      isUnmounted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socketRef.current?.close();
    };
  }, [token]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !smsThreadId) return;

    joinActiveThread(socket);

    if (socket.readyState === WebSocket.CONNECTING) {
      const handleOpen = () => joinActiveThread(socket);
      socket.addEventListener("open", handleOpen);
      return () => socket.removeEventListener("open", handleOpen);
    }
  }, [smsThreadId]);

  return socketRef;
};
